import {Link} from 'react-router-dom';
import {useEffect} from 'react';
import {
  ArrowRight,
  BadgePercent,
  ArrowUpRight,
  Building2,
  ClipboardCheck,
  HeartPulse,
  HelpCircle,
  type LucideIcon,
  Microscope,
  Phone,
  PiggyBank,
} from 'lucide-react';
import {COVERAGE_ICON_POOL, RESOURCE_ICONS} from '@client/pages/public/enroll/coverageIcons';
import {siteCareCards} from '@common/enrollmentSite/defaults/careCards';
import {careCardGridClass} from '@client/pages/public/enroll/pages/landingGrid';
import {POSTHOG_UNMASK_CLASS} from '@common-web/lib/analytics';
import {useEnrollGroup} from '@client/pages/public/enroll/enrollContext';
import {useEnrollPreview, useLinkHref} from '@client/pages/public/enroll/enrollPreview';
import {StepVideo} from '@client/pages/public/enroll/components/StepVideo';
import {EnrollEvent, useEnrollAnalytics} from '@client/pages/public/enroll/enrollAnalytics';
import {AppDownloadBanner} from '@client/pages/public/enroll/components/appDownload';
import {
  accessCodeEntryMode,
  groupOffersMedical,
  groupRequiresAccessCode,
} from '@client/pages/public/enroll/types';

// {codeTerm} is replaced with the group's access-code term at render; {medical} reads
// "limited medical" on sites without regular medical.
const ENROLL_STEPS = [
  {title: 'Enter your code', body: 'Have your {codeTerm} from your {contact} ready.'},
  {title: 'Choose your plans', body: 'Compare {medical}, dental, and vision side-by-side.'},
  {title: 'Review & submit', body: 'Add any dependents and submit — about 5 minutes.'},
];

// Dropdown-mode groups pick their location from a list instead of entering a code.
const DROPDOWN_ENROLL_STEPS = [
  {title: 'Find your location', body: 'Pick your location from the list.'},
  {title: 'Choose your plans', body: 'Compare {medical}, dental, and vision side-by-side.'},
  {title: 'Review & submit', body: 'Add any dependents and submit — about 5 minutes.'},
];

// Public groups (no access code) open with a "get started" step instead of the code step.
const NO_CODE_ENROLL_STEPS = [
  {title: 'Get started', body: 'Click Enroll Now to jump right in.'},
  {title: 'Choose your plans', body: 'Compare {medical}, dental, and vision side-by-side.'},
  {title: 'Review & submit', body: 'Add any dependents and submit — about 5 minutes.'},
];

const PRESCRIPTION_PROGRAM = {
  eyebrow: 'Prescriptions',
  title: 'Our prescription program',
  body: 'Covered conditions include: allergies, arthritis, diabetes, heart disease, high blood pressure, mental health, thyroid conditions, and more.',
};

const SMART_VIRTUAL_CARE = {
  eyebrow: "Know what's app-ening",
  title: 'Clever Health Smart Virtual Care',
  tagline: 'better, faster, easier — at the tips of your fingers',
  featureTitle: 'Virtual Urgent Care',
  featureBadge: '$0 per visit',
  features: [
    'On-demand 24/7, anywhere',
    'Smart medical questionnaire, phone, or video',
    '6-minute average diagnosis time',
    'Discount Rx card — 44,000+ scripts for $10 or less',
  ],
};

const GOODBILL = {
  eyebrow: 'Goodbill · Claims Audit',
  titleLead: 'Extra money is playing ',
  titleAccent: 'hide & seek',
  titleTail: ' in your claims',
  body: 'Through our Goodbill partnership, every facility claim gets a coding-error review, clinical-necessity review, and 501(r) financial-assistance screening at no cost to you.',
  footnoteLead: '47% of all claims contain errors. 95% are never reviewed.',
  footnoteTail: ' Concierge finds them.',
  stats: [
    {Icon: PiggyBank, value: 'Up to 80%', label: 'Plan savings off billed charges'},
    {Icon: BadgePercent, value: '20–30%', label: 'Average claim savings'},
    {
      Icon: Building2,
      value: '3,500+',
      label: 'Hospitals enabled for financial-assistance screening',
    },
    {
      Icon: ClipboardCheck,
      value: '$0 threshold',
      label: 'All claims get a full audit by licensed experts',
    },
  ] as {Icon: LucideIcon; value: string; label: string}[],
};

export function EnrollLanding() {
  const group = useEnrollGroup();
  const {track} = useEnrollAnalytics();
  const base = `/${group.slug}`;
  const {isPreview} = useEnrollPreview();
  const linkHref = useLinkHref();
  const phoneDigits = group.supportPhone.replace(/[^\d]/g, '');
  const codeTerm = group.accessCodeTerm ?? 'access code';
  const contact = group.benefitsContact ?? 'branch HR rep';
  const medicalKind = groupOffersMedical(group) ? 'medical' : 'limited medical';
  const requiresCode = groupRequiresAccessCode(group);
  const careCards = siteCareCards(group);
  // Only code-mode groups have something to "have handy" before starting.
  const typesCode = requiresCode && accessCodeEntryMode(group) === 'code';
  // Public groups skip the code screen — CTAs and the step list drop straight into the flow.
  const entryPath = requiresCode ? `${base}/access-code` : `${base}/medical`;
  const enrollSteps = !requiresCode
    ? NO_CODE_ENROLL_STEPS
    : typesCode
      ? ENROLL_STEPS
      : DROPDOWN_ENROLL_STEPS;

  useEffect(() => {
    track(EnrollEvent.LandingViewed);
  }, [track]);

  return (
    <div className={`space-y-16 ${POSTHOG_UNMASK_CLASS}`}>
      {/* Hero */}
      <section
        className={group.landingVideoUrl ? 'grid items-center gap-10 lg:grid-cols-2' : 'max-w-4xl'}
      >
        <div>
          <h1 className='mb-3 text-4xl font-bold leading-tight tracking-tight text-[var(--enroll-ink)] lg:text-5xl'>
            {group.heroHeadline.map((segment, index) => (
              <span
                key={index}
                className={segment.accent ? 'text-[var(--enroll-accent)]' : undefined}
              >
                {segment.text}
              </span>
            ))}
          </h1>
          <p className='mb-4 text-xl italic text-neutral-700'>{group.heroTagline}</p>
          <p className='mb-6 max-w-2xl text-lg text-neutral-500'>{group.heroBlurb}</p>
          <Link
            to={entryPath}
            className='inline-flex h-12 items-center gap-2 rounded-lg bg-[var(--enroll-accent)] px-7 text-base font-semibold text-white transition-colors hover:bg-[var(--enroll-accent-hover)]'
          >
            Enroll Now
            <ArrowRight className='h-[18px] w-[18px]' />
          </Link>
          <p className='mt-3 text-xs text-neutral-500'>
            {typesCode && `Have your ${codeTerm} handy. `}Takes about 5 minutes from start to
            finish.
          </p>
        </div>
        {group.landingVideoUrl && <StepVideo url={group.landingVideoUrl} />}
      </section>

      {/* Download the app — high-visibility entry point near the top (most members are on mobile) */}
      <AppDownloadBanner />

      {/* How to enroll — open section (header + steps on the background), links to the full walkthrough */}
      <section className='space-y-6'>
        <div className='flex items-center justify-between gap-4'>
          <h2 className='text-3xl font-semibold text-[var(--enroll-ink)]'>How to enroll</h2>
          <Link
            to={`${base}/how-to-enroll`}
            className='inline-flex flex-none items-center gap-1 text-sm font-semibold text-[var(--enroll-accent)] hover:underline'
          >
            See full steps
            <ArrowRight className='h-4 w-4' />
          </Link>
        </div>
        <div className='grid gap-4 sm:grid-cols-3'>
          {enrollSteps.map((step, index) => (
            <div
              key={step.title}
              className='flex gap-3 rounded-2xl border border-neutral-200 bg-white p-5'
            >
              <span className='inline-flex h-8 w-8 flex-none items-center justify-center rounded-full bg-[var(--enroll-accent)] text-sm font-bold text-white'>
                {index + 1}
              </span>
              <div>
                <h3 className='font-semibold text-neutral-900'>{step.title}</h3>
                <p className='mt-0.5 text-sm text-neutral-500'>
                  {step.body
                    .replaceAll('{codeTerm}', codeTerm)
                    .replaceAll('{contact}', contact)
                    .replaceAll('{medical}', medicalKind)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Real care, when you need it */}
      <section className='space-y-6'>
        <h2 className='text-3xl font-semibold text-[var(--enroll-ink)]'>
          Real care, when you need it
        </h2>
        <div className={`grid gap-4 ${careCardGridClass(careCards.length)}`}>
          {careCards.map(({icon, color, title, body}, i) => {
            const {Icon} = COVERAGE_ICON_POOL[icon];
            return (
              <div
                key={`${title}-${i}`}
                className='space-y-3 rounded-2xl border border-neutral-200 bg-white p-6'
              >
                <span
                  className='inline-flex h-10 w-10 items-center justify-center rounded-lg text-white'
                  style={{backgroundColor: color}}
                >
                  <Icon className='h-5 w-5' />
                </span>
                <h3 className='text-base font-semibold text-neutral-900'>{title}</h3>
                <p className='text-sm leading-relaxed text-neutral-600'>
                  {body.replaceAll('{phone}', group.supportPhone)}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Plans rooted in flexibility and affordability */}
      <section className='space-y-6'>
        <div>
          <p className='text-xs font-bold uppercase tracking-[0.18em] text-[var(--enroll-accent)]'>
            What's offered this year
          </p>
          <h2 className='mt-1 text-3xl font-semibold text-[var(--enroll-ink)]'>
            Plans rooted in flexibility and affordability
          </h2>
        </div>
        <div className='grid gap-5 md:grid-cols-2 lg:grid-cols-3'>
          {group.coverageCards.map((card) => {
            const {Icon} = COVERAGE_ICON_POOL[card.icon];
            const color = card.color;
            return (
              <div
                key={card.title}
                className='space-y-4 rounded-2xl border border-neutral-200 bg-white p-6'
              >
                <div className='flex items-center gap-3'>
                  <span
                    className='inline-flex h-9 w-9 items-center justify-center rounded-full text-white'
                    style={{backgroundColor: color}}
                  >
                    <Icon className='h-[18px] w-[18px]' />
                  </span>
                  <h3 className='text-lg font-semibold text-neutral-900'>{card.title}</h3>
                </div>
                <div className='h-px bg-neutral-200' />
                <ul className='space-y-2'>
                  {card.bullets.map((bullet) => (
                    <li key={bullet} className='flex gap-2 text-sm text-neutral-600'>
                      <span
                        className='mt-1.5 h-1.5 w-1.5 flex-none rounded-full'
                        style={{backgroundColor: color}}
                      />
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      {/* Our prescription program — open section */}
      <section className='flex flex-col gap-4 sm:flex-row sm:items-start'>
        <span className='inline-flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-[#7c3aed] text-white'>
          <Microscope className='h-6 w-6' />
        </span>
        <div className='space-y-1'>
          <p className='text-xs font-bold uppercase tracking-[0.18em] text-neutral-400'>
            {PRESCRIPTION_PROGRAM.eyebrow}
          </p>
          <h2 className='text-2xl font-semibold text-[var(--enroll-ink)]'>
            {PRESCRIPTION_PROGRAM.title}
          </h2>
          <p className='text-sm text-neutral-600'>{PRESCRIPTION_PROGRAM.body}</p>
          <p className='text-xs text-neutral-500'>
            Questions about your prescriptions? Call the Concierge Team at{' '}
            <a href={`tel:${phoneDigits}`} className='font-semibold text-[var(--enroll-ink)]'>
              {group.supportPhone}
            </a>
            .
          </p>
        </div>
      </section>

      {/* Clever Health Smart Virtual Care */}
      <section className='relative overflow-hidden rounded-2xl bg-[var(--enroll-ink)] p-8 text-white lg:p-10'>
        <span
          aria-hidden
          className='pointer-events-none absolute right-0 top-0 h-72 w-72 -translate-y-1/4 translate-x-1/4 rounded-full bg-[var(--enroll-accent)] opacity-20'
        />
        <div className='relative'>
          <p className='text-xs font-bold uppercase tracking-[0.18em] text-[var(--enroll-accent)]'>
            {SMART_VIRTUAL_CARE.eyebrow}
          </p>
          <h2 className='mt-1 text-3xl font-semibold'>{SMART_VIRTUAL_CARE.title}</h2>
          <p className='mb-6 italic text-white/70'>{SMART_VIRTUAL_CARE.tagline}</p>

          <div className='rounded-xl bg-white/5 p-6 backdrop-blur-sm'>
            <div className='mb-4 flex items-center justify-between gap-3'>
              <div className='flex items-center gap-2'>
                <span className='inline-flex h-7 w-7 items-center justify-center rounded-md bg-[var(--enroll-accent)] text-white'>
                  <HeartPulse className='h-4 w-4' />
                </span>
                <span className='font-semibold'>{SMART_VIRTUAL_CARE.featureTitle}</span>
              </div>
              <span className='rounded-full bg-white px-3 py-1 text-xs font-semibold text-[var(--enroll-ink)]'>
                {SMART_VIRTUAL_CARE.featureBadge}
              </span>
            </div>
            <ul className='space-y-1.5 text-sm text-white/80'>
              {SMART_VIRTUAL_CARE.features.map((feature) => (
                <li key={feature}>· {feature}</li>
              ))}
            </ul>
          </div>

          <div className='mt-6'>
            <p className='text-xs text-white/50'>
              In a crisis? Call or text the 988 Suicide &amp; Crisis Lifeline, or chat{' '}
              <a
                href='https://988lifeline.org'
                target='_blank'
                rel='noreferrer'
                className='underline'
              >
                988lifeline.org
              </a>
              .
            </p>
          </div>
        </div>
      </section>

      {/* Goodbill · Claims Audit — open section */}
      <section className='grid gap-8 lg:grid-cols-2'>
        <div className='space-y-3'>
          <p className='text-xs font-bold uppercase tracking-[0.18em] text-[var(--enroll-accent)]'>
            {GOODBILL.eyebrow}
          </p>
          <h2 className='text-3xl font-semibold text-[var(--enroll-ink)]'>
            {GOODBILL.titleLead}
            <span className='text-[var(--enroll-accent)]'>{GOODBILL.titleAccent}</span>
            {GOODBILL.titleTail}
          </h2>
          <p className='text-sm text-neutral-600'>{GOODBILL.body}</p>
          <p className='text-sm text-neutral-500'>
            <span className='italic'>{GOODBILL.footnoteLead}</span>
            {GOODBILL.footnoteTail}
          </p>
        </div>
        <div className='grid gap-3 sm:grid-cols-2'>
          {GOODBILL.stats.map(({Icon, value, label}) => (
            <div key={value} className='flex items-start gap-3 rounded-2xl bg-[#8cbae8]/30 p-4'>
              <span className='inline-flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-[#4a79bc] text-white'>
                <Icon className='h-[18px] w-[18px]' />
              </span>
              <div>
                <div className='text-lg font-bold text-[var(--enroll-ink)]'>{value}</div>
                <div className='text-xs text-neutral-500'>{label}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Enroll in your 2026 benefits */}
      <section className='relative overflow-hidden rounded-2xl bg-card p-10 text-center shadow-sm'>
        <span
          aria-hidden
          className='pointer-events-none absolute -bottom-16 -left-16 h-56 w-56 rounded-full bg-[#4a79bc] opacity-10'
        />
        <span
          aria-hidden
          className='pointer-events-none absolute -right-12 -top-12 h-44 w-44 rounded-full bg-[var(--enroll-accent)] opacity-20'
        />
        <div className='relative'>
          <p className='text-xs font-bold uppercase tracking-[0.18em] text-[var(--enroll-accent)]'>
            Ready when you are
          </p>
          <h2 className='mt-1 text-4xl font-semibold text-[var(--enroll-ink)]'>
            Enroll in your 2026 benefits
          </h2>
          <p className='mx-auto mt-3 max-w-xl text-neutral-500'>
            {typesCode &&
              `Have your ${codeTerm} from your ${group.benefitsContact ?? 'HR rep'} handy. `}
            The whole flow takes about 5 minutes — compare plans side-by-side, add dependents, and
            submit.
          </p>
          <Link
            to={entryPath}
            className='mt-6 inline-flex h-12 items-center gap-2 rounded-lg bg-[var(--enroll-accent)] px-7 text-base font-semibold text-white transition-colors hover:bg-[var(--enroll-accent-hover)]'
          >
            Enroll Now
            <ArrowRight className='h-[18px] w-[18px]' />
          </Link>
          <p className='mt-3 text-xs text-neutral-500'>
            {typesCode ? "Don't have a code? Call" : 'Questions? Call'} the Concierge Team at{' '}
            <a href={`tel:${phoneDigits}`} className='font-semibold text-[var(--enroll-ink)]'>
              {group.supportPhone}
            </a>
            {requiresCode ? ` or contact your ${contact}.` : '.'}
          </p>
        </div>
      </section>

      {/* Want to read up first? (optional per group) */}
      {!group.resourcesHidden && group.resourcesSection?.items.length ? (
        <section className='space-y-5'>
          <div>
            <h2 className='text-3xl font-semibold text-[var(--enroll-ink)]'>
              {group.resourcesSection.title || 'Want to read up first?'}
            </h2>
            <p className='mt-1 max-w-2xl text-sm text-neutral-500'>
              {group.resourcesSection.intro}
            </p>
          </div>
          <div className='grid gap-4 sm:grid-cols-2'>
            {group.resourcesSection.items.map((item, i) => {
              const {Icon} = RESOURCE_ICONS[item.icon];
              const className =
                'group flex items-start gap-3 rounded-xl border border-neutral-200 bg-card p-5 transition-colors hover:border-[var(--enroll-accent)]';
              const body = (
                <>
                  <span className='inline-flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-[var(--enroll-primary)]/10 text-[var(--enroll-primary)]'>
                    <Icon className='h-[18px] w-[18px]' />
                  </span>
                  <div className='flex-1'>
                    <div className='font-semibold text-neutral-900'>{item.title}</div>
                    <div className='text-sm text-neutral-500'>{item.description}</div>
                  </div>
                  {item.external ? (
                    <ArrowUpRight
                      className='h-4 w-4 flex-none text-neutral-400 transition-colors group-hover:text-[var(--enroll-accent)]'
                      aria-label='Opens in a new tab'
                    />
                  ) : (
                    <ArrowRight className='h-4 w-4 flex-none text-neutral-400 transition-all group-hover:translate-x-0.5 group-hover:text-[var(--enroll-accent)]' />
                  )}
                </>
              );
              // The builder preview runs in its own in-memory router: a plain link to one of
              // this site's pages would load that path on the internal portal instead.
              return isPreview && item.href.startsWith(`${base}/`) ? (
                <Link key={i} to={item.href} className={className}>
                  {body}
                </Link>
              ) : (
                <a
                  key={i}
                  href={linkHref(item.href)}
                  target={item.external ? '_blank' : undefined}
                  rel={item.external ? 'noreferrer' : undefined}
                  className={className}
                >
                  {body}
                </a>
              );
            })}
          </div>
        </section>
      ) : null}

      {/* Questions about your benefits? */}
      <section className='flex flex-col items-start gap-4 rounded-xl border border-neutral-200 bg-card p-6 sm:flex-row sm:items-center sm:justify-between'>
        <div className='flex items-start gap-3'>
          <span className='inline-flex h-10 w-10 flex-none items-center justify-center rounded-full bg-[var(--enroll-accent)] text-white'>
            <HelpCircle className='h-5 w-5' />
          </span>
          <div>
            <div className='font-semibold text-neutral-900'>Questions about your benefits?</div>
            <p className='text-sm text-neutral-500'>
              The Concierge Team can walk you through plan options, dependents, and what's right for
              your family. We're here every business day.
            </p>
          </div>
        </div>
        <a
          href={`tel:${phoneDigits}`}
          className='inline-flex h-11 flex-none items-center gap-2 rounded-lg bg-[var(--enroll-accent)] px-5 text-sm font-semibold text-white transition-colors hover:bg-[var(--enroll-accent-hover)]'
        >
          <Phone className='h-4 w-4' />
          {group.supportPhone}
        </a>
      </section>
    </div>
  );
}
